import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiArrowRight, FiPhone, FiSearch, FiPackage } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import productsData from '../data/products.json';

export default function Products() {
  const { categories, featuredProduct } = productsData;
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');

  const active = categories[activeTab];
  const filteredProducts = search.trim()
    ? categories.flatMap(c => c.products.filter(p => p.name.toLowerCase().includes(search.toLowerCase())))
    : active.products;

  return (
    <>
      <Helmet>
        <title>Products | Avivan Enterprises — FMCG Distribution Partner India</title>
        <meta name="description" content="Explore Avivan Enterprises' full FMCG product portfolio — Spices & Masalas, Chocolates, Rice, Fresh Fruits, Vegetables, and Grocery items across Tamil Nadu." />
      </Helmet>

      {/* Page Banner */}
      <div className="page-banner">
        <div className="container">
          <div className="page-banner-content text-center">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(243,156,18,0.2)', color: '#f39c12', padding: '6px 18px', borderRadius: 50, fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, border: '1px solid rgba(243,156,18,0.3)' }}>
              🛒 Product Portfolio
            </div>
            <h1>Our Products</h1>
            <p>Six categories, infinite quality — from spices and chocolates to fresh produce and grocery essentials</p>
            <div className="breadcrumb-c" style={{ justifyContent: 'center' }}>
              <Link to="/">Home</Link>
              <span className="sep">/</span>
              <span style={{ color: 'var(--accent)' }}>Products</span>
            </div>
          </div>
        </div>
      </div>

      {/* Category Overview Cards */}
      <section style={{ padding: '80px 0', background: 'var(--light)' }}>
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge"><FiPackage size={12} /> What We Distribute</span>
            <h2 className="section-title">Six Categories, <span>Infinite Quality</span></h2>
            <p className="section-subtitle">A complete FMCG distribution portfolio serving every business type across Tamil Nadu</p>
            <div className="divider-bar" />
          </div>
          <div className="cat-cards-grid">
            {categories.map((cat, i) => (
              <div className="cat-card" key={cat.id} data-aos="fade-up" data-aos-delay={i * 60}>
                <div className="cat-card-header" style={{ borderBottom: `2px solid ${cat.color}18` }}>
                  <div className="cat-emoji-box" style={{ background: cat.colorLight }}>
                    <span>{cat.emoji}</span>
                  </div>
                  <div>
                    <h4 className="cat-card-title">{cat.name}</h4>
                    <div style={{ marginTop: 5 }}>
                      {cat.status === 'active'
                        ? <span className="badge-active">Distributing Now</span>
                        : <span className="badge-coming-soon">Coming Soon</span>}
                    </div>
                  </div>
                </div>
                <div className="cat-card-body">
                  <p className="cat-card-desc">{cat.desc}</p>
                  <ul className="cat-product-list">
                    {cat.products.map(p => (
                      <li className="cat-product-item" key={p.id}>
                        <span className="cat-item-emoji">{p.emoji}</span>{p.name}
                        <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{p.weight}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="tel:+919876543210" className="cat-enquire-btn">
                    <FiPhone size={13} /> Enquire Now
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Search + Tab Browser */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="section-header" data-aos="fade-up">
            <span className="section-badge">🔍 Browse Products</span>
            <h2 className="section-title">Browse Our <span>Full Catalogue</span></h2>
            <div className="divider-bar" />
          </div>

          {/* Search */}
          <div style={{ maxWidth: 480, margin: '0 auto 40px', position: 'relative' }} data-aos="fade-up">
            <FiSearch style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '1.1rem' }} />
            <input
              className="form-control-c"
              style={{ paddingLeft: 44 }}
              placeholder="Search products across all categories..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {!search && (
            /* Category Tabs */
            <div className="product-tabs" data-aos="fade-up">
              {categories.map((cat, i) => (
                <button
                  key={cat.id}
                  className={`product-tab-btn ${activeTab === i ? 'active' : ''}`}
                  onClick={() => setActiveTab(i)}
                  style={activeTab === i ? { background: cat.color, borderColor: cat.color } : {}}
                >
                  <span>{cat.emoji}</span> {cat.name}
                  {cat.status === 'active' && <span className="tab-live-dot" />}
                </button>
              ))}
            </div>
          )}

          {/* Products Grid */}
          {search && (
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: 32, fontSize: '0.9rem' }}>
              {filteredProducts.length} result{filteredProducts.length !== 1 ? 's' : ''} for "<strong>{search}</strong>"
            </p>
          )}

          {filteredProducts.length > 0 ? (
            <div className="row g-3" data-aos="fade-up" data-aos-delay="80">
              {filteredProducts.map(p => (
                <div className="col-lg-3 col-md-4 col-sm-6" key={p.id}>
                  <div className="product-detail-card">
                    <div className="pdc-icon-box">
                      <span style={{ fontSize: '2.4rem' }}>{p.emoji}</span>
                    </div>
                    <div className="pdc-body">
                      <div className="pdc-name">{p.name}</div>
                      <div className="pdc-desc">{p.desc}</div>
                      <div className="pdc-weight">{p.weight}</div>
                      <a href="tel:+919876543210" className="pdc-cta">
                        <FiPhone size={12} /> Get Price
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '3rem', marginBottom: 12 }}>🔍</div>
              <p>No products found for "{search}". Try a different search term.</p>
            </div>
          )}
        </div>
      </section>

      {/* Featured Product — Aachi Masala */}
      <section style={{ padding: '80px 0', background: 'var(--gradient-hero)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 280, height: 280, background: 'rgba(255,255,255,0.03)', borderRadius: '50%' }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row align-items-center g-5">
            <div className="col-lg-6" data-aos="fade-right">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(243,156,18,0.2)', color: '#f39c12', padding: '6px 16px', borderRadius: 50, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, border: '1px solid rgba(243,156,18,0.3)' }}>
                🌶️ Featured Product
              </span>
              <h2 style={{ color: '#fff', fontWeight: 900, fontSize: 'clamp(1.7rem, 3.5vw, 2.4rem)', lineHeight: 1.2, marginBottom: 14 }}>
                {featuredProduct.name}
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.8, fontSize: '0.93rem', marginBottom: 24 }}>
                {featuredProduct.desc}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, marginBottom: 28 }}>
                {featuredProduct.highlights.map(h => (
                  <li key={h} style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'rgba(255,255,255,0.85)', fontSize: '0.88rem', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <span style={{ color: '#f39c12', flexShrink: 0 }}>✓</span> {h}
                  </li>
                ))}
              </ul>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 28 }}>
                {featuredProduct.specs.map(s => (
                  <div key={s.label} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '12px 16px' }}>
                    <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' }}>{s.label}</div>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--accent)', marginTop: 2 }}>{s.value}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <a href="tel:+919876543210" className="btn-accent-c" style={{ fontSize: '1rem', padding: '14px 36px' }}>
                  <FiPhone size={16} /> Order Now
                </a>
                <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="btn-outline-white" style={{ fontSize: '1rem', padding: '12px 34px' }}>
                  <FaWhatsapp size={18} /> WhatsApp Us
                </a>
              </div>
            </div>
            <div className="col-lg-6 d-none d-lg-block" data-aos="fade-left" data-aos-delay="150">
              <div className="fp-visual">
                <div style={{ fontSize: '5rem', marginBottom: 12 }}>🌶️</div>
                <div style={{ fontFamily: 'Poppins', fontWeight: 900, fontSize: '1.4rem', color: '#fff', marginBottom: 4 }}>Aachi Masala</div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', marginBottom: 22 }}>Complete Range · Authorized Distributor</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
                  {categories[0].products.map(p => (
                    <span key={p.id} style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.82)', padding: '6px 12px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600, border: '1px solid rgba(255,255,255,0.12)' }}>
                      {p.emoji} {p.name}
                    </span>
                  ))}
                </div>
                <div style={{ background: 'rgba(243,156,18,0.12)', border: '1px solid rgba(243,156,18,0.25)', borderRadius: 12, padding: '12px 18px' }}>
                  <div style={{ color: '#f39c12', fontWeight: 700, fontSize: '0.83rem' }}>✅ 100% Authentic · FSSAI Certified · Pan Tamil Nadu Supply</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dealer CTA */}
      <section style={{ padding: '80px 0', background: 'var(--light)' }}>
        <div className="container">
          <div style={{ textAlign: 'center' }} data-aos="fade-up">
            <h2 style={{ fontSize: 'clamp(1.8rem,3.5vw,2.5rem)', fontWeight: 800, marginBottom: 14 }}>
              Interested in <span style={{ color: 'var(--primary)' }}>Any of These Products?</span>
            </h2>
            <p style={{ color: 'var(--text-muted)', maxWidth: 500, margin: '0 auto 32px', lineHeight: 1.75 }}>
              Get in touch with our team for pricing, MOQ details, delivery schedules, and dealer partnership information.
            </p>
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/become-dealer" className="btn-primary-c" style={{ fontSize: '1rem', padding: '14px 36px' }}>
                Become a Dealer <FiArrowRight size={16} />
              </Link>
              <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="btn-green-c" style={{ fontSize: '1rem', padding: '14px 36px' }}>
                <FaWhatsapp size={18} /> Enquire on WhatsApp
              </a>
              <Link to="/contact" className="btn-outline-c" style={{ fontSize: '1rem', padding: '12px 34px' }}>
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
